
export class GlError extends Error {}

const ATTRIBUTES: WebGLContextAttributes = {
  alpha: false,
  antialias: false,
  depth: true,
  stencil: false,
  premultipliedAlpha: false,
  preserveDrawingBuffer: false,
  powerPreference: "high-performance",
};

/** Acquire a WebGL2 context, or throw with the reason. */
export function createContext(canvas: HTMLCanvasElement): WebGL2RenderingContext {
  const gl = canvas.getContext("webgl2", ATTRIBUTES);
  if (gl === null) {
    throw new GlError(
      "WebGL2 is unavailable (no context); the browser may be blocking it or the GPU is blacklisted",
    );
  }
  return gl;
}

export function probeWebGl2(): boolean {
  if (typeof document === "undefined") return false;
  let gl: WebGL2RenderingContext | null = null;
  try {
    gl = document.createElement("canvas").getContext("webgl2", ATTRIBUTES);
  } catch {
    return false;
  }
  if (gl === null) return false;
  // Hand the driver its context back rather than waiting for the probe canvas to
  // be collected: browsers cap live contexts, and on a phone that cap is small
  // enough that a leaked probe can cost the real renderer its context.
  gl.getExtension("WEBGL_lose_context")?.loseContext();
  return true;
}

export function checkGlError(gl: WebGL2RenderingContext, stage: string): void {
  const code = gl.getError();
  if (code === gl.NO_ERROR) return;
  throw new GlError(`${stage}: GL error 0x${code.toString(16)}`);
}
